<script setup lang="ts">
  import { computed, type ComputedRef, onMounted, ref, watch } from 'vue'
  import SelectField, { type Option } from '@/components/admin/form/SelectField.vue'
  import * as yup from 'yup'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase as Case } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import type { GenericObject } from 'vee-validate'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { RelationshipTypeEnum, EntityTypeEnum } from '@/types/generated/types.gen'

  const AdvowareRVGChoices = {
    BRAGO: { value: 0, label: 'BRAGO' },
    RVG_2004: { value: 1, label: 'RVG 2004' },
    RVG_2006: { value: 2, label: 'RVG 2006' },
    PAUSCHALE: { value: 3, label: 'Pauschale' },
    RVG_2013: { value: 8, label: 'RVG 2013' },
    STBVV: { value: 9, label: 'StBVV' },
    ZEITHONORAR: { value: 10, label: 'Zeithonorar' },
    AKTE_NICHT_ABRECHNEN: { value: 14, label: 'Akte nicht abrechnen' },
    KANZLEISPEZIFISCHE_RECHNUNG: { value: 15, label: 'kanzleispezifische Rechnung' },
    RVG_2021: { value: 16, label: 'RVG 2021' },
    STBVV_2025: { value: 21, label: 'StBVV 2025' }
  } as const

  const props = defineProps<{
    debounceDelay?: number
    throttleInterval?: number
    isOpponentExported?: boolean
    selectedEntitiesRelationships?: Array<string>
    mappedEntityTypes?: Array<string>
  }>()

  const emit = defineEmits(['update:payload'])

  const { t } = useTranslation({
    en: {
      rubrum: 'Rubrum',
      lawField: 'Law Field',
      lawTopic: 'Law Topic',
      clerk: 'Clerk',
      rvg: 'RVG Type',
      creationDate: 'Creation Date',
      advowareSection: 'Advoware Information',
      RubrumIsRequired: 'Rubrum is required',
      LawFieldIsRequired: 'Law Field is required',
      LawTopicIsRequired: 'Law Topic (labeled as "Wegen" in Advoware) is required',
      ClerkIsRequired: 'Clerk is required',
      warningTitle: 'Warning',
      advowareWarning: 'Some fields from Advoware are unkown and/or corrupted.',
      unkownValueText: '[UNKNOWN VALUE]'
    },
    de: {
      rubrum: 'Rubrum',
      lawField: 'Rechtsgebiet',
      lawTopic: 'Wegen',
      clerk: 'Sachbearbeiter',
      rvg: 'RVG-Art',
      creationDate: 'Erstellungsdatum',
      advowareSection: 'Advoware Information',
      RubrumIsRequired: 'Rubrum ist erforderlich',
      LawFieldIsRequired: 'Rechtsgebiet ist erforderlich',
      LawTopicIsRequired: 'Rechtsthema (in Advoware als „Wegen“ bezeichnet) ist erforderlich',
      ClerkIsRequired: 'Sachbearbeiter ist erforderlich',
      warningTitle: 'Warnung',
      advowareWarning: 'Einige Felder von Advoware sind nicht bekannt und/oder beschädigt.',
      unkownValueText: '[UNBEKANNTER WERT]'
    }
  })

  const showAdvowareWarning = ref(false)
  const rubrumManuallyEdited = ref(false)

  const caseStore = useCaseStore()
  const caseRecord = computed<Case | null>(() => caseStore.caseRecord)

  const transformAdvowareLawFieldOptions = (data: any[]): Option[] => {
    return data.map((item) => {
      const [kuerzelPair, referatPair] = item
      return {
        value: kuerzelPair[1],
        title: referatPair[1]
      }
    })
  }

  const transformAdvowareClerkOptions = (data: any[]): Option[] => {
    return data.map((item) => {
      const kuerzel = item.find((pair: string[]) => pair[0] === 'kuerzel')[1]
      const name = item.find((pair: string[]) => pair[0] === 'name')[1]
      if (!name) showAdvowareWarning.value = true
      return {
        value: kuerzel,
        title: name ? name : t('unkownValueText')
      }
    })
  }

  const advowareLawFieldOptions: ComputedRef<Option[]> = computed(() => {
    return transformAdvowareLawFieldOptions(caseRecord.value?.org.advoware_law_field_options as any)
  })

  const advowareClerkOptions: ComputedRef<Option[]> = computed(() => {
    return transformAdvowareClerkOptions(caseRecord.value?.org.advoware_clerk_options as any)
  })

  const rvgOptions: Option[] = [
    {
      value: null,
      title: 'RVG 2025'
    },
    ...Object.values(AdvowareRVGChoices).map((choice) => ({
      value: choice.value,
      title: choice.label
    }))
  ]

  const schema = {
    rubrum: yup.string().required(t('RubrumIsRequired')),
    lawField: yup.string().required(t('LawFieldIsRequired')),
    lawTopic: yup.string().required(t('LawTopicIsRequired')),
    clerk: yup.string().required(t('ClerkIsRequired'))
  }

  const isClientEntity = (entity: any) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r: any) => r.relationship_type === RelationshipTypeEnum.ENTITIES_CLIENT
    )
  }

  const isOpponentEntity = (entity: any) => {
    if (!entity || !entity.relationships) return false
    return entity.relationships.some(
      (r: any) => r.relationship_type === RelationshipTypeEnum.ENTITIES_OPPONENT
    )
  }

  const getClientEntity = computed(() => {
    if (!caseRecord.value?.data?.entities) return null
    return caseRecord.value.data.entities.find((entity) => isClientEntity(entity))
  })

  const getOpponentEntity = computed(() => {
    if (!caseRecord.value?.data?.entities) return null
    return caseRecord.value.data.entities.find((entity) => isOpponentEntity(entity))
  })

  const initialRubrum = computed(() => {
    if (!caseRecord.value || !caseRecord.value.data) return ''

    const clientEntity = getClientEntity.value
    const opponentEntity = getOpponentEntity.value
    const mappedTypes = props.mappedEntityTypes || ['client'] // Default to client

    if (!clientEntity) return ''

    // Format client name based on entity type
    const clientName =
      clientEntity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
        ? `${clientEntity.entity?.company_name || ''} ${clientEntity.entity?.company_legal_form || 'GmbH'}`
        : clientEntity.entity?.last_name || ''

    // Default title with just the client
    let title = `${clientName} ./.`

    // Check if opponent is included in mapped entity types and exists
    const includeOpponent = opponentEntity && mappedTypes.includes('opponent')

    if (includeOpponent) {
      // Format opponent name based on entity type
      const opponentName =
        opponentEntity.entity?.entity_type === EntityTypeEnum.LEGAL_PERSON
          ? `${opponentEntity.entity?.company_name || ''} ${opponentEntity.entity?.company_legal_form || 'GmbH'}`
          : opponentEntity.entity?.last_name || ''

      title = `${clientName} ./. ${opponentName}`
    }

    return title
  })

  const initialLawField = computed(() => {
    if (!caseRecord.value || !caseRecord.value.data) return ''

    const caseLawField = caseRecord.value.data.law.field || ''
    const matchingOption = advowareLawFieldOptions.value.find(
      (option) => option.title === caseLawField
    )
    if (matchingOption) return matchingOption.value

    // If no exact match, look for a partial match
    const partialMatch = advowareLawFieldOptions.value.find((option) => {
      if (!option.title) {
        showAdvowareWarning.value = true
        option.title = t('unkownValueText')
      }
      return option.title ? option.title.toLowerCase().includes(caseLawField.toLowerCase()) : false
    })
    return partialMatch ? partialMatch.value : ''
  })

  watch(initialLawField, (newValue) => {
    formData.lawField = newValue
  })

  const initialLawTopic = computed(() => {
    return caseRecord.value?.data?.law.topic || ''
  })

  watch(initialLawTopic, (newValue) => {
    formData.lawTopic = newValue
  })

  const setClerk = (firstName: string, lastName: string): string => {
    const fullName = `${firstName} ${lastName}`.toLowerCase()

    // Check for a direct match (full name)
    const directMatch = advowareClerkOptions.value.find(
      (option) => option.title.toLowerCase() === fullName
    )
    if (directMatch) return directMatch.value as string

    // If no direct match, check for a match with the last name
    const lastNameMatch = advowareClerkOptions.value.find((option) => {
      if (!option.title) {
        showAdvowareWarning.value = true
        option.title = t('unkownValueText')
      }
      return option.title ? option.title.toLowerCase().includes(lastName.toLowerCase()) : false
    })
    if (lastNameMatch) return lastNameMatch.value as string

    return ''
  }

  const initialClerk = computed(() => {
    if (!caseRecord.value?.clerk) return ''
    return setClerk(caseRecord.value.clerk.first_name, caseRecord.value.clerk.last_name) || ''
  })

  watch(initialClerk, (newValue) => {
    formData.clerk = newValue
  })

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors
  } = useFormWithValidation(
    {
      rubrum: initialRubrum.value,
      lawField: initialLawField.value,
      lawTopic: initialLawTopic.value,
      clerk: initialClerk.value,
      rvg: null
    },
    schema
  )

  watch(initialRubrum, (newValue) => {
    if (!rubrumManuallyEdited.value) {
      formData.rubrum = newValue
    }
  })

  watch(
    () => props.mappedEntityTypes,
    () => {
      if (!rubrumManuallyEdited.value) {
        formData.rubrum = initialRubrum.value
      }
      handleSave(formData)
    },
    { deep: true }
  )

  watch(
    () => caseRecord.value?.data?.entities,
    () => {
      if (!rubrumManuallyEdited.value) {
        formData.rubrum = initialRubrum.value
      }
      handleSave(formData)
    },
    { deep: true }
  )

  onMounted(() => {
    handleSave({
      rubrum: initialRubrum.value,
      lawField: initialLawField.value,
      lawTopic: initialLawTopic.value,
      clerk: initialClerk.value,
      rvg: null
    })
  })
  defineExpose({
    validate,
    showErrors
  })

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:payload', updatedData)
  }

  watch(
    formData,
    (newValue) => {
      handleSave(newValue)
    },
    { deep: true }
  )
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold text-gray-900">{{ t('advowareSection') }}</h2>
    <AutoSavingForm
      :debounce-delay="debounceDelay"
      :throttle-interval="throttleInterval"
      class="space-y-6"
      @save="handleSave"
    >
      <div class="space-y-6">
        <div
          v-if="showAdvowareWarning"
          class="fixed left-1/2 top-4 z-50 w-96 -translate-x-1/2 transform rounded border-l-4 border-orange-500
            bg-orange-100 p-4 text-orange-700 shadow-md transition-all duration-500 ease-in-out"
          role="alert"
        >
          <div class="flex">
            <div class="py-1">
              <svg
                class="mr-4 h-6 w-6 fill-current text-orange-500"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
              >
                <path
                  d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z"
                />
              </svg>
            </div>
            <div>
              <p class="font-bold">{{ t('warningTitle') }}</p>
              <p class="text-sm">
                {{ t('advowareWarning') }}
              </p>
            </div>
          </div>
        </div>
        <div>
          <InputField
            v-model="formData.rubrum"
            type="text"
            :label="t('rubrum')"
            name="rubrum"
            class="w-full"
            :error="formErrors?.value?.rubrum ?? null"
            @update:model-value="
              (value: any) => {
                rubrumManuallyEdited = !!value
                handleFieldChange('rubrum', value)
              }
            "
            @focus="() => handleFieldTouch('rubrum')"
          />
        </div>
        <div>
          <SelectField
            v-model="formData.lawField"
            :options="advowareLawFieldOptions"
            name="lawField"
            :label="t('lawField')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors?.value?.lawField ?? null"
            @update:model-value="(value: any) => handleFieldChange('lawField', value)"
            @focus="() => handleFieldTouch('lawField')"
          />
        </div>
        <div>
          <InputField
            v-model="formData.lawTopic"
            type="text"
            :label="t('lawTopic')"
            name="lawTopic"
            class="w-full"
            :error="formErrors?.value?.lawTopic ?? null"
            @update:model-value="(value: any) => handleFieldChange('lawTopic', value)"
            @focus="() => handleFieldTouch('lawTopic')"
          />
        </div>
        <div>
          <SelectField
            v-model="formData.clerk"
            :options="advowareClerkOptions"
            name="clerk"
            :label="t('clerk')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors?.value?.clerk ?? null"
            @update:model-value="(value: any) => handleFieldChange('clerk', value)"
            @focus="() => handleFieldTouch('clerk')"
          />
        </div>
        <div>
          <SelectField
            v-model="formData.rvg"
            :options="rvgOptions"
            name="rvg"
            :label="t('rvg')"
            :is-filterable="true"
            class="w-full"
            @update:model-value="(value: any) => handleFieldChange('rvg', value)"
          />
        </div>
      </div>
    </AutoSavingForm>
  </div>
</template>

<style scoped>
  .bg-gray-50 {
    background-color: #f9fafb;
  }

  h2 {
    color: #111827;
  }

  :deep(.form-label) {
    font-weight: 500;
    color: #374151;
    margin-bottom: 0.5rem;
    display: block;
  }

  :deep(.form-select),
  :deep(.form-input) {
    width: 100%;
    border-radius: 0.375rem;
    border-color: #d1d5db;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #111827;
    background-color: #ffffff;
  }

  :deep(.form-select:focus),
  :deep(.form-input:focus) {
    outline: 2px solid transparent;
    outline-offset: 2px;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
</style>
